import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PageHeader, Spinner } from "@/components/shared";
import Watchtower, { mergeWatchtowers } from "@/components/workflow/Watchtower";
import LifecycleBoardView, { PHASES, loadLifecycleBoards } from "@/components/workflow/LifecycleBoardView";
import { AnalysisExceptionQueue } from "@/components/ops/AnalysisExceptionQueue";

const Ico = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };
const ACCENTS = {
  blue: "bg-blue-50 text-blue-600", indigo: "bg-indigo-50 text-indigo-600", amber: "bg-amber-50 text-amber-600",
  emerald: "bg-emerald-50 text-emerald-600", red: "bg-red-50 text-red-600", violet: "bg-violet-50 text-violet-600", slate: "bg-slate-100 text-slate-500",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
const money = (v) => { if (v == null) return "—"; const n = Number(v); if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (n >= 1e4) return `$${(n / 1e3).toFixed(0)}k`; return `$${n.toLocaleString()}`; };

// Deep-link map: summary KPI key → target board + pre-applied stage/filter query.
const KPI_ROUTES = {
  open_leads: { path: "/sales/board" },
  client_projects: { path: "/client-success" },
  proposals_out: { path: "/sales/board", stage: "proposal_sent" },
  eng_unsigned: { path: "/sales/board", stage: "engagement_sent" },
  organizers_open: { path: "/client-success", stage: "organizer_assigned" },
  docs_review: { path: "/client-success", stage: "documents_received" },
  prod_waiting: { path: "/production", filter: "attention" },
  ready_file: { path: "/production", stage: "ready_to_file" },
  urgent: { path: "/production", filter: "attention" },
};

// Section 1 — compact executive KPI chip
function Kpi({ icon, value, label, accent, onClick }) {
  return (
    <button onClick={onClick} data-testid={`fo-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="text-left flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all shrink-0 min-w-[150px]">
      <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${ACCENTS[accent] || ACCENTS.slate}`}><Ico name={icon} className="h-4 w-4" /></span>
      <span className="leading-tight"><span className="block text-lg font-bold text-slate-900">{value ?? 0}</span><span className="block text-[10px] text-slate-400">{label}</span></span>
    </button>
  );
}

// Section 4 — compact stat widget
function StatWidget({ icon, value, label, accent, onClick }) {
  return (
    <button onClick={onClick} data-testid={`fo-widget-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="text-left bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all">
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-2 ${ACCENTS[accent] || ACCENTS.slate}`}><Ico name={icon} className="h-4 w-4" /></div>
      <div className="text-2xl font-semibold text-slate-900 leading-none">{value ?? 0}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </button>
  );
}

// Section 4 — small list widget
function ListWidget({ icon, title, rows, empty, render }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm" data-testid={`fo-list-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2.5"><Ico name={icon} className="h-3.5 w-3.5 text-slate-400" />{title}</div>
      {(!rows || rows.length === 0) ? <div className="text-[12px] text-slate-400">{empty}</div> : (
        <div className="space-y-2">{rows.map((r, i) => <div key={i}>{render(r)}</div>)}</div>
      )}
    </div>
  );
}

// Section 2c — Unassigned Meetings review queue (self-contained; renders null when empty)
const CONF_BADGE = {
  high: { label: "High Confidence Match", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  possible: { label: "Possible Match", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-400" },
  none: { label: "No Match Found", cls: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-300" },
};
const STARS = { high: 5, medium: 4, low: 3 };

function ConfStars({ level }) {
  const filled = STARS[level] || 3;
  return (
    <span className="inline-flex items-center gap-0.5" title={`${level} confidence`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Icons.Star key={i} className={`h-3 w-3 ${i < filled ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

function fmtMeetingWhen(z, created) {
  const iso = z?.started_at || z?.scheduled_at || created;
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} • ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function UnmatchedTranscriptsWidget() {
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get("transcript");
  const autoOpenedRef = useRef(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState({});          // { [tid]: searchString }
  const [results, setResults] = useState({}); // { [tid]: [contacts] }
  const [selected, setSelected] = useState({}); // { [tid]: {id,title,type,source} }
  const [assigning, setAssigning] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/meeting-transcripts/unmatched"); setItems(data.items || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while any meeting's Zoom enrichment or AI extraction is still running.
  const anyPending = items.some((t) => {
    const mc = t.meeting_context || {};
    const s = [mc.zoom?.enrichment_status, mc.transcript?.extraction_status];
    return s.some((v) => v === null || v === undefined || v === "not_started" || v === "processing");
  });
  useEffect(() => {
    if (!anyPending) return;
    let n = 0;
    const iv = setInterval(() => { n += 1; load(); if (n >= 8) clearInterval(iv); }, 4000);
    return () => clearInterval(iv);
  }, [anyPending, load]);

  useEffect(() => {
    if (targetId && targetId !== autoOpenedRef.current && items.some((i) => i.id === targetId)) {
      autoOpenedRef.current = targetId;
      setTimeout(() => document.querySelector(`[data-testid="fo-unmatched-${targetId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    }
  }, [targetId, items]);

  const search = async (tid, val) => {
    setQ((p) => ({ ...p, [tid]: val }));
    if (val.trim().length < 2) { setResults((p) => ({ ...p, [tid]: [] })); return; }
    try { const { data } = await api.get(`/search?q=${encodeURIComponent(val.trim())}`); setResults((p) => ({ ...p, [tid]: (data.results || []).filter((r) => r.type === "contact") })); }
    catch { setResults((p) => ({ ...p, [tid]: [] })); }
  };
  const assign = async (tid) => {
    const sel = selected[tid];
    if (!sel) return;
    setAssigning(tid);
    try {
      await api.post(`/meeting-transcripts/${tid}/assign`, { client_id: sel.id });
      toast.success(`Meeting assigned to ${sel.title} — Allison is analyzing it…`);
      setSelected((p) => { const n = { ...p }; delete n[tid]; return n; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    finally { setAssigning(null); }
  };
  const dismiss = async (tid) => {
    setDismissing(tid);
    try { await api.post(`/meeting-transcripts/${tid}/dismiss`); toast.success("Meeting dismissed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); }
    finally { setDismissing(null); }
  };

  if (items.length === 0) return null;
  const provIcon = (p) => (p === "fireflies" ? "Sparkles" : "Video");

  return (
    <div className="bg-white border border-amber-200 rounded-xl p-4 shadow-sm" data-testid="fo-unmatched-transcripts">
      <div className="flex items-start gap-2 mb-3">
        <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0"><Icons.CalendarClock className="h-4 w-4" /></span>
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-slate-800">Unassigned Meetings <span className="text-amber-600">· {items.length}</span></h2>
          <p className="text-[11px] text-slate-400">Allison couldn't confidently match these meetings to a Lead or Client. Review the available meeting details below and assign the correct record.</p>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((t) => {
          const mc = t.meeting_context || {};
          const z = mc.zoom || {}; const tx = mc.transcript || {}; const matching = mc.matching || {};
          const conf = matching.confidence || "none";
          const badge = CONF_BADGE[conf] || CONF_BADGE.none;
          const pending = ["not_started", "processing", null, undefined].includes(tx.extraction_status);
          const matches = matching.suggested_matches || [];
          const sel = selected[t.id];
          const when = fmtMeetingWhen(z, t.created_at);
          const detParts = (z.participants && z.participants.length)
            ? z.participants.map((p) => ({ name: p.display_name, email: p.email, fromTranscript: false }))
            : [
              ...(tx.detected_names || []).map((n) => ({ name: n, fromTranscript: true })),
              ...(tx.detected_emails || []).filter((e) => !(tx.detected_names || []).length).map((e) => ({ email: e, fromTranscript: true })),
            ];
          const qv = q[t.id] || ""; const res = results[t.id] || [];

          return (
            <div key={t.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`fo-unmatched-${t.id}`}>
              {/* Confidence badge strip */}
              <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${badge.cls}`} data-testid={`fo-unmatched-badge-${t.id}`}>
                  <span className={`h-2 w-2 rounded-full ${badge.dot}`} />{badge.label}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Ico name={provIcon(t.provider)} className="h-3.5 w-3.5" /><span className="capitalize">{t.provider}</span></span>
              </div>

              <div className="p-3">
                {/* Header */}
                <div className="mb-2.5">
                  <div className="text-sm font-bold text-slate-800">{z.topic || t.title || "Meeting"}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                    {when && <span className="inline-flex items-center gap-1"><Icons.Clock className="h-3 w-3" />{when}</span>}
                    {z.duration_minutes ? <span>· {z.duration_minutes} minutes</span> : null}
                    {(z.host_name || z.host_email) ? <span>· Hosted by {z.host_name || z.host_email}</span> : null}
                  </div>
                </div>

                {/* Meeting details */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 mb-2.5">
                  {t.zoom_meeting_id && <span><span className="text-slate-400">Meeting ID:</span> {t.zoom_meeting_id}</span>}
                  <span><span className="text-slate-400">Transcript:</span> <span className="text-emerald-600 font-medium">Available</span></span>
                  {z.enrichment_status === "complete" || z.enrichment_status === "partial"
                    ? <span><span className="text-slate-400">Zoom details:</span> <span className="text-emerald-600 font-medium">Loaded</span></span>
                    : z.enrichment_status === "processing" || z.enrichment_status === "not_started"
                      ? <span className="inline-flex items-center gap-1 text-slate-400"><Icons.Loader2 className="h-3 w-3 animate-spin" />Loading Zoom details…</span>
                      : null}
                </div>

                {/* Transcript preview */}
                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 mb-2.5" data-testid={`fo-unmatched-preview-${t.id}`}>
                  {pending
                    ? <div className="text-[12px] text-slate-400 flex items-center gap-1.5"><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Identifying meeting details…</div>
                    : (
                      <>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Detected from transcript</div>
                        <div className="text-[12px] text-slate-600 italic leading-relaxed">“{tx.preview || "No preview available."}”</div>
                      </>
                    )}
                </div>

                {/* Detected topics */}
                {!pending && (tx.detected_topics || []).length > 0 && (
                  <div className="mb-2.5" data-testid={`fo-unmatched-topics-${t.id}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Detected Topics</div>
                    <div className="flex flex-wrap gap-1.5">
                      {tx.detected_topics.map((tag, i) => (
                        <span key={i} className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Participants */}
                {detParts.length > 0 && (
                  <div className="mb-2.5" data-testid={`fo-unmatched-participants-${t.id}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Participants{detParts.some((p) => p.fromTranscript) ? " (detected from transcript)" : ""}</div>
                    <div className="space-y-0.5">
                      {detParts.slice(0, 5).map((p, i) => (
                        <div key={i} className="text-[12px] text-slate-600">{p.name}{p.name && p.email ? " · " : ""}{p.email && <span className="text-slate-400">{p.email}</span>}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Possible matches */}
                {matches.length > 0 && (
                  <div className="mb-2.5" data-testid={`fo-unmatched-suggestions-${t.id}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Possible Matches <span className="normal-case font-normal text-slate-300">— clues only, confirm before assigning</span></div>
                    <div className="space-y-1">
                      {matches.map((s) => {
                        const isSel = sel?.id === s.id;
                        return (
                          <button key={s.id} onClick={() => setSelected((p) => ({ ...p, [t.id]: { id: s.id, title: s.title, type: s.type, source: "suggestion" } }))}
                            data-testid={`fo-unmatched-suggestion-${s.id}`}
                            className={`w-full text-left flex items-center justify-between gap-2 rounded-lg border px-3 py-2 transition-colors ${isSel ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40"}`}>
                            <span className="min-w-0 flex items-center gap-2">
                              {isSel ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Icons.Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                              <span className="min-w-0">
                                <span className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium text-slate-800 truncate">{s.title}</span>
                                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${s.type === "Client" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"}`}>{s.type}</span>
                                </span>
                                <span className="block text-[11px] text-slate-400 truncate">{s.subtitle} · {s.reason}</span>
                              </span>
                            </span>
                            <ConfStars level={s.confidence} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Manual search */}
                <div className="mb-2.5">
                  <input value={qv} onChange={(e) => search(t.id, e.target.value)} placeholder="Search by name, email, phone, or company…"
                    data-testid={`fo-unmatched-search-${t.id}`} className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                  {res.length > 0 && (
                    <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto">
                      {res.map((r) => {
                        const isSel = sel?.id === r.id;
                        return (
                          <button key={r.id} onClick={() => setSelected((p) => ({ ...p, [t.id]: { id: r.id, title: r.title, type: r.subtype || "Lead", source: "search" } }))}
                            data-testid={`fo-unmatched-result-${r.id}`}
                            className={`w-full text-left flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${isSel ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-amber-300 hover:bg-amber-50"}`}>
                            <span className="min-w-0 flex items-center gap-2">
                              {isSel ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Icons.Circle className="h-4 w-4 text-slate-300 shrink-0" />}
                              <span className="min-w-0"><span className="block text-sm text-slate-800 truncate">{r.title}</span><span className="block text-[11px] text-slate-400 truncate">{r.subtitle}</span></span>
                            </span>
                            <Icons.ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {qv.trim().length >= 2 && res.length === 0 && <div className="mt-1.5 text-[12px] text-slate-400">No matching records.</div>}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
                  <button onClick={() => dismiss(t.id)} disabled={dismissing === t.id} data-testid={`fo-unmatched-dismiss-${t.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5 disabled:opacity-50">
                    {dismissing === t.id ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.EyeOff className="h-3.5 w-3.5" />}Dismiss / Ignore
                  </button>
                  <button onClick={() => assign(t.id)} disabled={!sel || assigning === t.id} data-testid={`fo-unmatched-assign-${t.id}`}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40 disabled:cursor-not-allowed">
                    {assigning === t.id ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.UserPlus className="h-3.5 w-3.5" />}
                    {sel ? `Assign to ${sel.title}` : "Select a record to assign"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function FirmOverview() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [intel, setIntel] = useState(null);
  const [activity, setActivity] = useState(null);
  const [boards, setBoards] = useState(null);
  const [aiQueue, setAiQueue] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [flagFilter, setFlagFilter] = useState(null);
  const boardRef = useRef(null);
  const aiQueueRef = useRef(null);
  const onWatchtowerRow = (row) => {
    setFlagFilter({ key: row.key, label: row.label });
    setTimeout(() => boardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const scrollToAiQueue = () => aiQueueRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const load = useCallback(async () => {
    try {
      const [s, i, t, b, q, ip] = await Promise.all([
        api.get("/overview/summary"), api.get("/overview/intelligence"),
        api.get("/timeline?limit=40"), loadLifecycleBoards(),
        api.get("/case-analysis/queue/pending"),
        api.get("/case-analysis/queue/in-progress"),
      ]);
      setSummary(s.data?.cards || []); setIntel(i.data); setActivity(t.data || []); setBoards(b);
      setAiQueue(q.data?.items || []);
      setInProgress(ip.data?.items || []);
    } catch (e) { toast.error("Couldn't load Firm Overview. Please retry."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll every 8 s while any analysis is still processing
  useEffect(() => {
    const anyProcessing = inProgress.some((a) => a.status === "processing");
    if (!anyProcessing) return;
    let n = 0;
    const iv = setInterval(() => { n += 1; load(); if (n >= 30) clearInterval(iv); }, 8000);
    return () => clearInterval(iv);
  }, [inProgress, load]);

  const watchtower = useMemo(() => boards ? mergeWatchtowers(PHASES.map((p) => boards[p.key]?.watchtower)) : null, [boards]);
  const derived = useMemo(() => {
    if (!boards) return { in_production: 0, completed: 0 };
    const pc = boards.production?.cards || [];
    return { in_production: pc.filter((c) => c.stage !== "completed").length, completed: pc.filter((c) => c.stage === "completed").length };
  }, [boards]);

  const c = intel?.counts || {};
  const drill = (r) => {
    const p = new URLSearchParams();
    if (r.stage) p.set("stage", r.stage);
    if (r.filter) p.set("filter", r.filter);
    const qs = p.toString();
    navigate(r.path + (qs ? `?${qs}` : ""));
  };

  const processingItems = inProgress.filter((a) => a.status === "processing");

  return (
    <div data-testid="firm-overview-page" className="space-y-6">
      <PageHeader title="Firm Overview" description="The firm's executive command center — performance, lifecycle, what needs attention, and Allison's recommendations." />

      {/* Section 1 — Executive KPI strip */}
      {!summary ? <Spinner /> : (
        <div className="flex gap-2.5 overflow-x-auto pb-1" data-testid="fo-kpi-strip">
          {summary.map((k) => <Kpi key={k.key} icon={k.icon} value={k.value} label={k.label} accent={k.accent} onClick={() => k.key === "ai_review" ? scrollToAiQueue() : drill(KPI_ROUTES[k.key] || { path: k.tab === "clients" ? "/client-success" : "/sales/board" })} />)}
          <Kpi icon="Factory" value={derived.in_production} label="In Production" accent="emerald" onClick={() => drill({ path: "/production" })} />
          <Kpi icon="CheckCircle2" value={derived.completed} label="Completed" accent="emerald" onClick={() => drill({ path: "/production", stage: "completed" })} />
          <Kpi icon="DollarSign" value={money(intel?.revenue_pipeline)} label="Revenue Pipeline" accent="indigo" onClick={() => navigate("/sales/estimates")} />
          <Kpi icon="Timer" value={`${watchtower?.avg_days_in_stage ?? 0}d`} label="Avg Days in Stage" accent="violet" onClick={() => drill({ path: "/production", filter: "attention" })} />
        </div>
      )}

      {/* Section 2 — Allison Watchtower */}
      {watchtower && <Watchtower data={watchtower} onRowClick={onWatchtowerRow} />}

      {/* Section 2b — AI Case Analysis needing review (hidden when empty) */}
      {aiQueue.length > 0 && (
        <div ref={aiQueueRef} className="bg-white border border-indigo-200 rounded-xl p-4 shadow-sm" data-testid="fo-ai-review-queue">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-7 w-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center"><Icons.Brain className="h-4 w-4" /></span>
            <div>
              <h2 className="text-sm font-bold text-slate-800">AI Analysis to Review</h2>
              <p className="text-[11px] text-slate-400">Allison finished these cases — approve to generate &amp; send the estimate</p>
            </div>
            <span className="ml-auto text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-full px-2.5 py-0.5" data-testid="fo-ai-review-count">{aiQueue.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {aiQueue.map((a) => (
              <button key={a.id} onClick={() => navigate(`/case-analysis/${a.client_id}`)}
                data-testid={`fo-ai-review-card-${a.client_id}`}
                className="text-left bg-slate-50 hover:bg-white border border-slate-200 hover:border-indigo-300 rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-semibold text-slate-900 text-sm truncate">{a.client_name}</span>
                  {a.needs_review
                    ? <span className="shrink-0 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Low confidence · {a.confidence}%</span>
                    : <span className="shrink-0 text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">{a.confidence}% confident</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                  {a.client_ref && <span>{a.client_ref}</span>}
                  {a.complexity && <span className="inline-flex items-center gap-1"><Icons.Gauge className="h-3 w-3" />{a.complexity}</span>}
                  <span className="inline-flex items-center gap-1"><Icons.ListChecks className="h-3 w-3" />{a.service_count} service{a.service_count === 1 ? "" : "s"}</span>
                  <span className="inline-flex items-center gap-1 text-slate-700 font-semibold"><Icons.DollarSign className="h-3 w-3" />{money(a.pricing_total)}</span>
                </div>
                <div className="flex items-center justify-between mt-2.5">
                  <span className="text-[10px] text-slate-400">{relTime(a.created_at)}</span>
                  <span className="text-[11px] font-semibold text-indigo-600 inline-flex items-center gap-1">Review & approve<Icons.ArrowRight className="h-3 w-3" /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Section 2b-i — Analyses in progress (hidden when empty) */}
      {processingItems.length > 0 && (
        <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm" data-testid="fo-analyses-processing">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-7 w-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <Icons.Loader2 className="h-4 w-4 animate-spin" />
            </span>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Allison Is Analyzing</h2>
              <p className="text-[11px] text-slate-400">These meetings are being processed — they'll appear in "AI Analysis to Review" when done</p>
            </div>
            <span className="ml-auto text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2.5 py-0.5">{processingItems.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {processingItems.map((a) => (
              <div key={a.id} data-testid={`fo-processing-card-${a.id}`}
                className="flex items-center gap-3 bg-blue-50/50 border border-blue-100 rounded-xl px-3.5 py-3">
                <Icons.Loader2 className="h-4 w-4 text-blue-400 animate-spin shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{a.client_name}</div>
                  <div className="text-[11px] text-slate-400">
                    {{"preparing":"Preparing transcript","researching":"Reviewing knowledge base","analyzing":"Reviewing meeting","finalizing":"Building recommendations"}[a.stage] || "Analyzing"}
                    {" · "}{relTime(a.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 2b-ii — Analysis Exception Queue (replaces static "Needs Retry" cards) */}
      <AnalysisExceptionQueue mode="dashboard" />

      {/* Section 2c — Unmatched meeting transcripts */}
      <UnmatchedTranscriptsWidget />

      {/* Section 3 — Firm Lifecycle board */}
      {!boards ? <Spinner /> : (
        <div ref={boardRef} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-7 w-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><Icons.Workflow className="h-4 w-4" /></span>
            <div><h2 className="text-sm font-bold text-slate-800">Firm Lifecycle</h2><p className="text-[11px] text-slate-400">Lead → Client → Production · drag within a phase to advance a stage</p></div>
          </div>
          <LifecycleBoardView boards={boards} setBoards={setBoards} flagFilter={flagFilter} onClearFlag={() => setFlagFilter(null)} />
        </div>
      )}

      {/* Section 4 — Operational Intelligence */}
      {intel && (
        <div className="space-y-3" data-testid="fo-operational-intelligence">
          <h2 className="text-sm font-bold text-slate-700">Operational Intelligence</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatWidget icon="UserPlus" value={c.new_leads_today} label="New Leads Today" accent="blue" onClick={() => drill({ path: "/sales/board", stage: "lead" })} />
            <StatWidget icon="Clock" value={c.waiting_on_client} label="Waiting on Client" accent="amber" onClick={() => drill({ path: "/production", filter: "attention" })} />
            <StatWidget icon="Users" value={c.waiting_on_staff} label="Waiting on Staff" accent="amber" onClick={() => drill({ path: "/production", stage: "internal_review" })} />
            <StatWidget icon="Send" value={c.ready_to_file} label="Returns Ready to File" accent="emerald" onClick={() => drill({ path: "/production", stage: "ready_to_file" })} />
            <StatWidget icon="FolderCheck" value={c.docs_awaiting_review} label="Docs Awaiting Review" accent="blue" onClick={() => drill({ path: "/client-success", stage: "documents_received" })} />
            <StatWidget icon="MessagesSquare" value={c.unread_portal_messages} label="Unread Portal Msgs" accent="violet" onClick={() => navigate("/communications/center")} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <ListWidget icon="CalendarClock" title="Upcoming Deadlines" rows={intel.upcoming_deadlines} empty="No deadlines in the next 14 days."
              render={(r) => (
                <div className="flex items-center justify-between text-[12px]"><span className="truncate text-slate-700">{r.name}{r.ref ? ` · ${r.ref}` : ""}</span><span className="text-amber-600 font-medium shrink-0 ml-2">{r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}</span></div>
              )} />
            <ListWidget icon="CreditCard" title="Recent Payments" rows={intel.recent_payments} empty="No recent payments."
              render={(r) => (
                <div className="flex items-center justify-between text-[12px]"><span className="truncate text-slate-700">{r.name}</span><span className="text-emerald-600 font-semibold shrink-0 ml-2">{money(r.amount)} · {relTime(r.date)}</span></div>
              )} />
            <ListWidget icon="FileSignature" title="Recent Engagements" rows={intel.recent_engagements} empty="No recent engagements."
              render={(r) => (
                <div className="flex items-center justify-between text-[12px]"><span className="truncate text-slate-700">{r.name}{r.ref ? ` · ${r.ref}` : ""}</span><span className="text-slate-400 shrink-0 ml-2 capitalize">{(r.status || "").replace(/_/g, " ")}</span></div>
              )} />
          </div>
        </div>
      )}

      {/* Section 5 — Recent Activity feed */}
      {activity && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="fo-activity-feed">
          <div className="flex items-center gap-1.5 text-sm font-bold text-slate-700 mb-3"><Icons.Activity className="h-4 w-4 text-slate-400" />Recent Activity</div>
          {activity.length === 0 ? <div className="text-[12px] text-slate-400">No recent activity yet.</div> : (
            <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
              {activity.map((a, i) => (
                <div key={i} className="flex items-start gap-2.5" data-testid="fo-activity-item">
                  <span className="h-6 w-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0 mt-0.5"><Ico name={a.icon || "Dot"} className="h-3 w-3" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-slate-700 leading-snug">{a.title}</div>
                    {a.description && <div className="text-[11px] text-slate-400 truncate">{a.description}</div>}
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0">{relTime(a.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
